import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ForecastingPanel = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('next_quarter');
  const [selectedScenario, setSelectedScenario] = useState('conservative');

  const periodOptions = [
    { value: 'next_month', label: 'Next Month' },
    { value: 'next_quarter', label: 'Next Quarter' },
    { value: 'next_year', label: 'Next Year' },
    { value: 'custom', label: 'Custom Period' }
  ];

  const scenarioOptions = [
    { value: 'conservative', label: 'Conservative' },
    { value: 'optimistic', label: 'Optimistic' },
    { value: 'pessimistic', label: 'Pessimistic' },
    { value: 'current_trend', label: 'Current Trend' }
  ];

  // Mock forecasting data
  const forecastData = {
    next_quarter: {
      conservative: {
        projected_spending: 1250000,
        confidence: 85,
        variance_range: { min: -8.5, max: 12.3 },
        key_factors: [
          'Seasonal spending patterns',
          'Historical variance trends',
          'Current budget utilization'
        ],
        recommendations: [
          'Consider increasing IT infrastructure budget by 15%',
          'Marketing spend may exceed allocated budget in Q1',
          'R&D projects likely to be under-utilized'
        ]
      },
      optimistic: {
        projected_spending: 1420000,
        confidence: 72,
        variance_range: { min: -5.2, max: 18.7 },
        key_factors: [
          'Growth initiatives funding',
          'Market expansion plans',
          'New product development'
        ],
        recommendations: [
          'Prepare contingency funding for growth initiatives',
          'Monitor marketing campaign ROI closely',
          'Consider accelerating R&D investment'
        ]
      }
    }
  };

  const currentForecast = forecastData?.[selectedPeriod]?.[selectedScenario] || forecastData?.next_quarter?.conservative;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-success';
    if (confidence >= 60) return 'text-warning';
    return 'text-error';
  };

  const handleRunForecast = () => {
    console.log('Running forecast with:', { selectedPeriod, selectedScenario });
    // Implement forecast calculation logic
  };

  const handleExportForecast = () => {
    console.log('Exporting forecast data...');
    // Implement export functionality
  };

  return (
    <div className="bg-card border border-border rounded-lg card-shadow">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Budget Forecasting</h3>
            <p className="text-sm text-muted-foreground">Predictive budget analysis</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Forecast Controls */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Forecast Period
            </label>
            <Select
              options={periodOptions}
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              placeholder="Select Period"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Scenario Model
            </label>
            <Select
              options={scenarioOptions}
              value={selectedScenario}
              onChange={setSelectedScenario}
              placeholder="Select Scenario"
            />
          </div>

          <div className="flex space-x-2">
            <Button
              variant="default"
              iconName="Play"
              onClick={handleRunForecast}
              className="flex-1"
            >
              Run Forecast
            </Button>
            <Button
              variant="outline"
              iconName="Download"
              onClick={handleExportForecast}
            >
              Export
            </Button>
          </div>
        </div>

        {/* Forecast Results */}
        <div className="space-y-4">
          <div className="bg-muted/10 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground">Projected Spending</h4>
              <div className={`text-sm font-medium ${getConfidenceColor(currentForecast?.confidence)}`}>
                {currentForecast?.confidence}% confidence
              </div>
            </div>
            
            <div className="text-2xl font-bold text-foreground mb-2">
              {formatCurrency(currentForecast?.projected_spending)}
            </div>

            <div className="text-xs text-muted-foreground">
              Variance Range: {currentForecast?.variance_range?.min}% to +{currentForecast?.variance_range?.max}%
            </div>

            {/* Confidence Indicator */}
            <div className="mt-3">
              <div className="flex justify-between items-center text-xs mb-1">
                <span className="text-muted-foreground">Confidence Level</span>
                <span className="font-medium">{currentForecast?.confidence}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${getConfidenceColor(currentForecast?.confidence)?.replace('text-', 'bg-')}`}
                  style={{ width: `${currentForecast?.confidence}%` }}
                />
              </div>
            </div>
          </div>

          {/* Key Factors */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
              <Icon name="Target" size={14} className="mr-2" />
              Key Factors
            </h4>
            <ul className="space-y-2">
              {currentForecast?.key_factors?.map((factor, index) => (
                <li key={index} className="flex items-center text-xs text-muted-foreground">
                  <Icon name="ChevronRight" size={12} className="mr-2 flex-shrink-0" />
                  {factor}
                </li>
              ))}
            </ul>
          </div>

          {/* Recommendations */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
              <Icon name="Lightbulb" size={14} className="mr-2" />
              Recommendations
            </h4>
            <div className="space-y-2">
              {currentForecast?.recommendations?.map((rec, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="AlertCircle" size={12} className="text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-muted-foreground">{rec}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-semibold text-foreground mb-3">Quick Actions</h4>
          <div className="grid grid-cols-1 gap-2">
            <Button
              variant="ghost"
              size="sm"
              iconName="Calendar"
              className="justify-start text-xs"
              onClick={() => console.log('Schedule budget review')}
            >
              Schedule Budget Review
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="FileText"
              className="justify-start text-xs"
              onClick={() => console.log('Generate forecast report')}
            >
              Generate Detailed Report
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Bell"
              className="justify-start text-xs"
              onClick={() => console.log('Set forecast alerts')}
            >
              Set Forecast Alerts
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForecastingPanel;